export type CompilerStepState = 'ok' | 'warning' | 'error';
export type CompilerFieldMatcher = string | RegExp;

const isRecord = (value: any): boolean => typeof value === 'object' && value !== null;

const hasMatchingFieldWithSeverity = (
  fieldMatchers: CompilerFieldMatcher[],
  errors: any,
  severity: 'error' | 'warning'
): boolean => {
  if (!isRecord(errors)) {
    return false;
  }

  for (const field in errors) {
    const fieldError = errors[field];
    // Check if field matches
    let matches = false;
    for (const matcher of fieldMatchers) {
      if (typeof matcher === 'string' && matcher === field) {
        matches = true;
        break;
      }
      if (matcher instanceof RegExp && matcher.test(field)) {
        matches = true;
        break;
      }
    }

    if (matches) {
      // Check if error has the requested severity
      if (isRecord(fieldError) && fieldError.severity === severity) {
        return true;
      }
      // If fieldError is a string or doesn't have severity property, treat as error
      if (!isRecord(fieldError) || !('severity' in fieldError)) {
        if (severity === 'error') {
          return true;
        }
      }
    }
  }

  return false;
};

export const getStateForFields = (fieldMatchers: CompilerFieldMatcher[], errors: any): CompilerStepState | undefined => {
  if (!isRecord(errors)) {
    return undefined;
  }

  if (hasMatchingFieldWithSeverity(fieldMatchers, errors, 'error')) {
    return 'error';
  }

  if (hasMatchingFieldWithSeverity(fieldMatchers, errors, 'warning')) {
    return 'warning';
  }

  return 'ok';
};

export default class CompilerStepsValidator {
  private steps: CompilerSingleStepErrorChecker[] = [];

  registerStep(stepNumber: number, configureStep: (options: CompilerSingleStepErrorChecker) => void) {
    const newStep = new CompilerSingleStepErrorChecker(stepNumber);
    configureStep(newStep);
    this.steps.push(newStep);
    return this;
  }

  getStepState(stepNumber: number, errors: any): CompilerStepState | undefined {
    const step = this.steps.find((s) => s.stepNumber === stepNumber);
    if (!step) {
      throw new Error(`Step ${stepNumber} not registered.`);
    }

    if (!isRecord(errors)) {
      return undefined;
    }

    if (step.hasErrorWithSeverity(errors, 'error')) {
      return 'error';
    }

    if (step.hasErrorWithSeverity(errors, 'warning')) {
      return 'warning';
    }

    this.ensureAllIssuesAreReflectedInSteps(errors);

    return 'ok';
  }

  isStepValid(stepNumber: number, errors: any): boolean | undefined {
    const stepState = this.getStepState(stepNumber, errors);
    if (stepState === undefined) {
      return undefined;
    }

    return stepState !== 'error';
  }

  private ensureAllIssuesAreReflectedInSteps(issues: any) {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }
    if (!isRecord(issues)) {
      return;
    }

    // Notifies in case a new error was added, and the developer forgot to associate it with a step.
    // This helps to detect the issue when a field displays an error but its step is still marked with green checkmark.
    for (const field in issues) {
      let isStepFound = false;
      for (const step of this.steps) {
        if (step.isIssueFromThisStep(field)) {
          isStepFound = true;
          break;
        }
      }

      if (!isStepFound) {
        throw new Error(`Error '${field}' does not correspond to any step. Please go and assign it.`);
      }
    }
  }
}

class CompilerSingleStepErrorChecker {
  public stepNumber: number;

  private fieldNames: string[] = [];
  private patterns: RegExp[] = [];

  constructor(stepNumber: number) {
    this.stepNumber = stepNumber;
  }

  withFields(...fieldNames: CompilerFieldMatcher[]) {
    for (const field of fieldNames) {
      if (typeof field === 'string') {
        this.fieldNames.push(field);
        continue;
      }
      this.patterns.push(field);
    }

    return this;
  }

  hasErrorWithSeverity(issues: any, severity: 'error' | 'warning'): boolean {
    if (!isRecord(issues)) {
      return false;
    }

    for (const field in issues) {
      if (this.isIssueFromThisStep(field)) {
        const fieldError = issues[field];
        // If error has severity property, check it
        if (isRecord(fieldError) && 'severity' in fieldError) {
          if (fieldError.severity === severity) {
            return true;
          }
        } else if (severity === 'error') {
          // If no severity property, treat as error
          return true;
        }
      }
    }

    return false;
  }

  isIssueFromThisStep(fieldName: string): boolean {
    if (this.fieldNames.includes(fieldName)) {
      return true;
    }

    for (const regExp of this.patterns) {
      if (fieldName.match(regExp)) {
        return true;
      }
    }
    return false;
  }
}
